import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

// oxlint-disable-next-line react/only-export-components
export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++nextId.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-20 md:bottom-5 left-4 right-4 md:left-auto md:right-5 md:w-84 z-[70] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={clsx(
              'pointer-events-auto flex items-center gap-2.5 p-3 rounded-xl shadow-xl border text-xs font-medium bg-surface animate-slide-up transition-all',
              toast.type === 'success' && 'border-state-confirmed/30 text-text-primary',
              toast.type === 'error' && 'border-state-cancelled/30 text-text-primary',
              toast.type === 'info' && 'border-brand-primary/30 text-text-primary',
            )}
          >
            {toast.type === 'success' && <CheckCircle size={16} className="text-state-confirmed shrink-0" />}
            {toast.type === 'error' && <AlertCircle size={16} className="text-state-cancelled shrink-0" />}
            {toast.type === 'info' && <Info size={16} className="text-brand-primary shrink-0" />}
            <span className="flex-1 leading-snug">{toast.message}</span>
            <button
              type="button"
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="text-text-muted hover:text-text-primary p-0.5 rounded cursor-pointer transition-colors"
              aria-label="Cerrar"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
